import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Navbar,
} from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { SITE_CONTAINER_FORM } from "@/lib/siteLayout";
import { RenderServiceBody } from "@/lib/renderServiceBody";
import { formatServicePrice } from "@/lib/servicePagePricing";
import { setPageMeta } from "@/lib/seo";
import api from "@/lib/api";
import { isServiceAvailable, serviceUnavailableMessage } from "@/lib/serviceAvailability";
import { useAuth } from "@/context/AuthContext";
import {
  Loader2,
  RotateCw,
  Wand2,
  Bot,
  Star,
  TrendingUp,
  Megaphone,
  BarChart3,
  Coins,
  Sparkles,
  ArrowRight,
} from "lucide-react";

const ICONS = {
  listing_basics: Coins,
  viewer_360: RotateCw,
  ai_photo_bg: Wand2,
  ai_customer_support: Bot,
  list_boost: Star,
  priority_ranking: TrendingUp,
  meta_ads: Megaphone,
  detailed_stats: BarChart3,
};

function sectionIcon(section) {
  if (section?.id && ICONS[section.id]) return ICONS[section.id];
  const k = section?.addon_key;
  if (k === "viewer_360") return RotateCw;
  if (k === "ai_photo_bg") return Wand2;
  if (k === "ai_customer_support") return Bot;
  if (k === "list_boost") return Star;
  if (k === "priority_ranking") return TrendingUp;
  if (k === "meta_ads") return Megaphone;
  if (k === "detailed_stats") return BarChart3;
  return Sparkles;
}

export default function Services() {
  const [config, setConfig] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/pricing-config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const page = config?.seller_services_page;
  const sections = (page?.sections || []).filter((s) => s.published !== false);

  useEffect(() => {
    setPageMeta({
      title: page?.hero_title || "Szolgáltatások",
      description: page?.hero_lead?.slice(0, 160),
      url: `${window.location.origin}/szolgaltatasok`,
    });
  }, [page?.hero_title, page?.hero_lead]);

  const goPrimary = () => {
    const path = page?.cta_primary_path || "/fiok/uj-hirdetes";
    if (path.includes("uj-hirdetes") && !user) navigate("/belepes");
    else navigate(path);
  };

  if (!config) {
    return (
      <div className="dark min-h-screen bg-anthracite-900 text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-electric" />
      </div>
    );
  }

  return (
    <div className="dark min-h-screen bg-anthracite-900 text-white">
      <Navbar />
      <main data-testid="services-page" className="pt-28 pb-16">
        <div className={SITE_CONTAINER_FORM}>
          <p className="text-[10px] uppercase tracking-[0.25em] text-electric mb-3">Eladóknak</p>
          <h1 className="font-heading text-4xl md:text-5xl font-medium tracking-tight">
            {page?.hero_title || "Szolgáltatások"}
          </h1>
          <p className="text-slate-400 text-lg mt-4 leading-relaxed max-w-2xl">
            {page?.hero_lead}
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <button
              type="button"
              data-testid="services-cta-primary"
              onClick={goPrimary}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-electric hover:bg-electric-light text-white font-medium transition-all"
            >
              {page?.cta_primary_label || "Új hirdetés"}
              <ArrowRight className="w-4 h-4" />
            </button>
            <Link
              to={page?.cta_secondary_path || "/kreditek"}
              data-testid="services-cta-secondary"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/15 hover:bg-white/5 text-slate-200 transition-all"
            >
              {page?.cta_secondary_label || "Kreditek"}
            </Link>
          </div>
        </div>

        <div className={`${SITE_CONTAINER_FORM} mt-16 space-y-6`}>
          {sections.map((sec) => {
            const Icon = sectionIcon(sec);
            const priceLine = formatServicePrice(sec, config);
            const addonKey = sec.addon_key;
            const svcOff = addonKey && !isServiceAvailable(config, addonKey);
            const svcOffMsg = svcOff ? serviceUnavailableMessage(config, addonKey) : "";
            return (
              <article
                key={sec.id}
                data-testid={`service-section-${sec.id}`}
                className={`glass rounded-2xl p-6 md:p-8 border ${svcOff ? "border-amber-500/25" : "border-white/10"}`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-electric/15 flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5 text-electric" strokeWidth={1.75} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="font-heading text-xl md:text-2xl font-medium flex flex-wrap items-center gap-2">
                      {sec.title}
                      {svcOff && (
                        <span
                          className="text-[10px] uppercase tracking-wider font-normal px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-200 border border-amber-500/30"
                          data-testid={`service-unavailable-${sec.id}`}
                        >
                          Átmenetileg nem elérhető
                        </span>
                      )}
                    </h2>
                    {sec.tagline && (
                      <p className="text-sm text-slate-400 mt-1">{sec.tagline}</p>
                    )}
                    {priceLine && (
                      <p className="text-xs font-mono text-electric mt-2" data-testid={`service-price-${sec.id}`}>
                        {priceLine}
                      </p>
                    )}
                    {svcOff && (
                      <p className="text-sm text-amber-200/90 mt-2" data-testid={`service-unavail-msg-${sec.id}`}>
                        {svcOffMsg}
                      </p>
                    )}
                    <div className="mt-5">
                      <RenderServiceBody text={sec.body} />
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </main>
      <Footer />
    </div>
  );
}
